import React from 'react';
import PropTypes from 'prop-types';

import {Box} from 'views/components';

const SectionSelector = (props) => {
  const {sections, currentIndex, transitionDuration=400} = props;
  const [displayedIndex, setDisplayedIndex] = React.useState(currentIndex);
  const [direction, setDirection] = React.useState('right'); // 'left' or 'right'
  const [animationStage, setAnimationStage] = React.useState('idle'); // 'idle', 'exit', 'enter'

  React.useEffect(() => {
    if (currentIndex !== displayedIndex) {
      // Determine transition direction
      setDirection(currentIndex > displayedIndex ? 'right' : 'left');
      setAnimationStage('exit');

      // After exit animation, change section and start enter animation
      const timer = setTimeout(() => {
        setDisplayedIndex(currentIndex);
        window.scrollTo(0, 0); // Scroll to top when new section loads
        setAnimationStage('enter');

        // After enter animation, finish transition
        const enterTimer = setTimeout(() => {
          setAnimationStage('idle');
        }, transitionDuration / 2);

        return () => clearTimeout(enterTimer);
      }, transitionDuration / 2);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, displayedIndex, transitionDuration]);

  // Ensure we have valid sections and index
  if (!sections || sections.length === 0) {
    return null;
  }

  const validIndex = Math.max(0, Math.min(displayedIndex, sections.length - 1));
  const currentSection = sections[validIndex].Component;

  // Calculate transform and opacity based on animation stage
  const getTransform = () => {
    if (animationStage === 'exit') {
      // Exit to the opposite direction we're going
      return `translateX(${direction === 'right' ? '-150px' : '150px'})`;
    } else if (animationStage === 'enter') {
      // Enter from the direction we're going
      return `translateX(${direction === 'right' ? '150px' : '-150px'})`;
    }
    return 'translateX(0)';
  };

  const getOpacity = () => {
    return animationStage === 'idle' ? 1 : 0;
  };

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        minHeight: 400, // Prevent layout shift during transitions
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          width: '100%',
          transform: getTransform(),
          opacity: getOpacity(),
          transition: `all ${transitionDuration / 2}ms ease-in-out`,
        }}
      >
        {currentSection}
      </Box>
    </Box>
  );
};

SectionSelector.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.object).isRequired,
  currentIndex: PropTypes.number.isRequired,
  transitionDuration: PropTypes.number,
};

export default SectionSelector;
